import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatShort, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader, QueueNo, UrgentBadge } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Info } from "lucide-react";

const ORANGE = "#FF5A00";
const ROSE = "#E11D48";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthBounds = (y, m) => [`${y}-${pad(m + 1)}-01`, isoDay(new Date(y, m + 1, 0))];
const yearsBack = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2, y - 3, y - 4].map(String); };
const num = (v, digits = 1) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: digits }));
const hrs = (v) => {
  if (v == null) return "—";
  if (v > 0 && v < 1) return `${Math.round(v * 60)} min`; 
  return `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: v < 100 ? 1 : 0 })} hrs`;
};
const pct = (v) => (v == null ? "—" : `${v}%`);
const tick = (key, group) => (group === "year" ? key : group === "month" ? `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}` : `${key.slice(8)}/${key.slice(5, 7)}`);

const HELP = {
  jobs: "Jobs with a dated AVT inspection row in this period: the AVT stage (filled in the app or carried from an import). A job inspected over several runs counts once.",
  good: "Labels that passed AVT inspection (issued less rejected), forwarded to Slitting.",
  reject: "Labels rejected at AVT inspection, against the total inspected.",
};

const valueLabel = (rows, field, color, size = 10) => function ValueLabel({ x, y, width, index }) {
  const row = rows[index];
  if (!row || !row[field]) return null;
  return <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={size} fontWeight={600} fill={color} className="font-mono-num">{formatShort(row[field], 1)}</text>;
};

/** Search any job (not just the current AVT queue) by BPR, customer or product — jumps straight to its AVT stage. */
function JobSearch() {
  const [text, setText] = useState("");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (text.trim().length < 2) {
      setHits([]);
      return undefined;
    }
    const id = ++seq.current;
    const timer = setTimeout(() => {
      api.get("/jobs", { params: { q: text.trim(), limit: 10 } })
        .then(({ data }) => id === seq.current && setHits(Array.isArray(data) ? data : data.items || []))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  return (
    <div className="relative" data-testid="avt-search">
      <Input
        value={text}
        placeholder="Search a job — BPR, customer or product"
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => setText(e.target.value)}
        className="rounded-sm h-9 text-sm"
        data-testid="avt-search-input"
      />
      {open && hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto border border-neutral-200 bg-white shadow-md">
          {hits.map((j) => (
            <Link key={j.id} to={`/orders/${j.id}?tab=production_flow&stage=avt`}
              onMouseDown={() => setOpen(false)}
              className="px-3 py-2 flex justify-between items-center hover:bg-orange-50 border-b border-neutral-100 last:border-0">
              <div>
                <div className="font-mono-num text-xs font-semibold whitespace-nowrap">{j.mpwo_number || j.job_number}</div>
                <div className="text-sm">{j.customer_name}</div>
              </div>
              <div className="text-xs text-neutral-500 truncate max-w-[40%] text-right">{j.product_name}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// The AVT queue, as the AVT desk's "Ready for Inspection": printed jobs, and Converted BPR jobs whose BPR Status
// is AVT — in the queue's order, numbered. (Status "avt" alone is a job AVT has finished.)
function Queue() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/jobs", { params: { ready: "printing,flat_bed", limit: 500 } }).then(({ data }) => setRows(data)).catch(() => setRows((r) => r || []));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", load);
  const list = Array.isArray(rows) ? rows : rows?.items || [];
  return (
    <div className="border border-neutral-200 bg-white" data-testid="avt-queue">
      <div className="px-4 py-2.5 border-b border-neutral-200 flex items-center justify-between">
        <div className="label-caps">Jobs waiting for AVT inspection</div>
        <div className="text-xs text-neutral-500 font-mono-num">{rows ? list.length : "…"}</div>
      </div>
      <div className="divide-y divide-neutral-100 max-h-72 overflow-y-auto">
        {list.map((j) => (
          <Link key={j.id} to={`/orders/${j.id}?tab=production_flow&stage=avt`}
            className={`px-4 py-2.5 flex justify-between items-center hover:bg-neutral-50 ${j.urgent_schedule ? "bg-rose-50/70" : ""}`}>
            <div>
              <div className="font-mono-num text-xs flex items-center gap-1.5">
                <QueueNo n={j.queue_no} />
                {j.urgent_schedule && <UrgentBadge urgent />}
                {j.mpwo_number || j.job_number}
              </div>
              <div className="text-sm">{j.customer_name}</div>
            </div>
            <div className="text-xs text-neutral-500 truncate max-w-[40%] text-right">{j.product_name}</div>
          </Link>
        ))}
        {rows && list.length === 0 && <div className="px-4 py-6 text-center text-neutral-400 text-sm">Nothing waiting for AVT right now.</div>}
        {!rows && <div className="px-4 py-6 text-center text-neutral-400 text-sm">Loading…</div>}
      </div>
    </div>
  );
}

export default function AvtAnalytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = isoDay(new Date());
  const [kind, setKind] = useState("all"); // all | year | month | day | range
  const [value, setValue] = useState({ year: today.slice(0, 4), month: today.slice(0, 7), day: today, from: today, to: today });
  const [from, to] = kind === "year" ? [`${value.year}-01-01`, `${value.year}-12-31`]
    : kind === "month" ? monthBounds(Number(value.month.slice(0, 4)), Number(value.month.slice(5, 7)) - 1)
    : kind === "day" ? [value.day, value.day]
    : kind === "range" ? [value.from <= value.to ? value.from : value.to, value.from <= value.to ? value.to : value.from]
    : ["", ""];
  const spanDays = kind === "range" ? (new Date(to) - new Date(from)) / 86_400_000 : null;
  const group = kind === "month" || kind === "day" || (kind === "range" && spanDays <= 62) ? "day" : "month";
  const [data, setData] = useState(null);
  const run = useRef(0);

  const load = () => {
    const n = ++run.current;
    api.get("/dashboard/avt", { params: { date_from: from || undefined, date_to: to || undefined, group } })
      .then(({ data: d }) => n === run.current && setData(d))
      .catch(() => n === run.current && setData((d) => d || { error: true }));
  };
  useEffect(() => { load(); }, [kind, value.year, value.month, value.day, value.from, value.to]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", load);

  const label = kind === "all" ? "All dates" : kind === "year" ? value.year : kind === "month" ? `${MONTHS[Number(value.month.slice(5, 7)) - 1]} ${value.month.slice(0, 4)}`
    : kind === "range" ? `${formatDate(from)} – ${formatDate(to)}` : formatDate(value.day);
  const t = data?.totals, a = data?.averages;
  // "All dates" with nothing picked can carry a handful of stray months from a mistyped date somewhere in the
  // sheets — those show as a lone "1" far off from the real activity and force every other month's label to
  // crowd together. Not hidden, just not let them stretch the whole axis: a month only counts as real activity
  // here once it clears a small floor.
  const rawSeries = data?.series || [];
  const firstReal = rawSeries.findIndex((s) => s.jobs >= 5);
  const series = kind === "all" && firstReal > 0 ? rawSeries.slice(firstReal) : rawSeries;
  const trimmedStray = series.length < rawSeries.length;

  return (
    <div>
      <PageHeader subtitle="Inspection Analytics" title="AVT Inspection" />
      <div className="p-8 space-y-4" data-testid="avt-analytics">
        <JobSearch />
        <Queue />

        <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2">
          <div className="label-caps">AVT Inspection · {label}</div>
          <div className="text-xs text-neutral-500">From each job's AVT inspection record (AVT stage, or the Production sheet import).</div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="avt-date-filter">
          {[["all", "All"], ["year", "Year"], ["month", "Month"], ["day", "Day"], ["range", "Date range"]].map(([k, name]) => (
            <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}
              className={`rounded-sm h-7 px-3 text-xs ${kind === k ? "bg-[#FF5A00] hover:bg-[#e04f00]" : ""}`} data-testid={`avt-kind-${k}`}>{name}</Button>
          ))}
          {kind === "year" && (
            <select value={value.year} onChange={(e) => setValue({ ...value, year: e.target.value })} data-testid="avt-year"
              className="border border-neutral-300 rounded-sm h-7 px-2 text-xs bg-white">
              {yearsBack().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          {kind === "month" && <Input type="month" value={value.month} onChange={(e) => e.target.value && setValue({ ...value, month: e.target.value })} data-testid="avt-month" className="rounded-sm w-40 h-7 text-xs" />}
          {kind === "day" && <Input type="date" value={value.day} onChange={(e) => e.target.value && setValue({ ...value, day: e.target.value })} data-testid="avt-day" className="rounded-sm w-40 h-7 text-xs" />}
          {kind === "range" && (
            <div className="flex items-center gap-1.5">
              <Input type="date" value={value.from} onChange={(e) => e.target.value && setValue({ ...value, from: e.target.value })} data-testid="avt-range-from" className="rounded-sm w-36 h-7 text-xs" />
              <span className="text-xs text-neutral-400">to</span>
              <Input type="date" value={value.to} onChange={(e) => e.target.value && setValue({ ...value, to: e.target.value })} data-testid="avt-range-to" className="rounded-sm w-36 h-7 text-xs" />
            </div>
          )}
        </div>

        {data?.error && <div className="text-sm text-neutral-500">AVT figures are not available.</div>}

        {data && !data.error && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <Kpi label="Jobs inspected" value={t.jobs.toLocaleString("en-IN")} sub={`on ${t.working_days.toLocaleString("en-IN")} days · ${t.shifts.toLocaleString("en-IN")} shifts`} help={HELP.jobs} testid="kpi-avt-jobs" />
              <Kpi label="Jobs inspected / day" value={num(a.jobs_per_day)} sub="per day with AVT" testid="kpi-avt-jobs-day" />
              <Kpi label="Jobs inspected / shift" value={num(a.jobs_per_shift)} sub="per shift with AVT" testid="kpi-avt-jobs-shift" />
              <Kpi label="Labels passed" value={num(t.good_u, 0)} sub={`in ${hrs(t.hours)}`} help={HELP.good} testid="kpi-avt-good" />
              <Kpi label="Labels rejected" value={num(t.waste_u, 0)} accent="text-rose-700" sub={`${pct(data.reject_pct)} of inspected`} help={HELP.reject} testid="kpi-avt-waste" />
              <Kpi label="Reject %" value={pct(data.reject_pct)} accent={data.reject_pct > 5 ? "text-rose-700" : undefined} testid="kpi-avt-reject-pct" />
              <Kpi label="Labels passed / day" value={num(a.good_u?.day, 0)} testid="kpi-avt-good-day" />
              <Kpi label="Labels passed / shift" value={num(a.good_u?.shift, 0)} testid="kpi-avt-good-shift" />
              <Kpi label="Labels passed / hour" value={num(a.good_u?.hour, 0)} testid="kpi-avt-good-hour" />
            </div>

            {data.excluded?.count > 0 && <Excluded ex={data.excluded} isAdmin={isAdmin} onIncluded={load} />}

            {trimmedStray && (
              <div className="border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-xs text-neutral-600" data-testid="avt-stray-note">
                A few stray months with only a handful of jobs (likely a mistyped date on one or two records) are left off the chart below
                so the real months aren't squeezed together. They're still counted in the totals above.
              </div>
            )}

            {t.sessions === 0 && (
              <div className="text-xs text-neutral-500" data-testid="avt-empty">
                No AVT inspection recorded in this period yet. It fills in from the AVT stage (date, times, operator, inspection rows).
              </div>
            )}

            {t.sessions > 0 && series.length > 0 && (
              <Chart title={`Jobs inspected, passed and rejected per ${group}`} testid="avt-chart-jobs"
                key2={<span className="inline-flex items-center gap-3 text-[11px] text-neutral-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 inline-block" style={{ background: ORANGE }} />passed</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 inline-block" style={{ background: ROSE }} />rejected</span></span>}>
                <BarChart data={series} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="key" fontSize={11} stroke="#71717A" tickFormatter={(k) => tick(k, group)} interval={series.length > 20 ? "preserveStartEnd" : 0} />
                  <YAxis fontSize={11} stroke="#71717A" tickFormatter={(v) => formatShort(v, 1)} />
                  <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={TOOLTIP_STYLE} labelFormatter={(k) => (group === "day" ? formatDate(k) : k)}
                    formatter={(v, name) => [Number(v).toLocaleString("en-IN"), name === "good_u" ? "Passed" : "Rejected"]} />
                  <Bar dataKey="good_u" stackId="labels" fill={ORANGE} />
                  <Bar dataKey="waste_u" stackId="labels" fill={ROSE}>
                    {series.length <= 20 && <LabelList dataKey="jobs" content={valueLabel(series, "jobs", "#9A3412", 9)} />}
                  </Bar>
                </BarChart>
              </Chart>
            )}

            {t.sessions > 0 && (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Table title="Shift-wise" testid="avt-shifts" rows={data.shifts} empty="No shifts in this period."
                  cols={[["Shift", (r) => r.name], ["Jobs", (r) => r.jobs], ["Hours", (r) => hrs(r.hours)], ["Passed", (r) => num(r.good_u, 0)], ["Rejected", (r) => num(r.waste_u, 0)], ["Reject %", (r) => pct(r.reject_pct)]]} />
                <div className="xl:col-span-2">
                  <Table title="Operator-wise" testid="avt-operators" rows={data.operators} empty="No operator data in this period." maxBodyHeight="28rem"
                    cols={[["Operator", (r) => r.name], ["Jobs", (r) => r.jobs], ["Runs", (r) => r.sessions], ["Hours", (r) => hrs(r.hours)], ["Passed", (r) => num(r.good_u, 0)], ["Rejected", (r) => num(r.waste_u, 0)], ["Reject %", (r) => pct(r.reject_pct)]]} />
                </div>
                <div className="xl:col-span-3">
                  <Table title="Machine-wise" testid="avt-machines" rows={data.machines} empty="No machine data in this period."
                    cols={[["Machine", (r) => r.name], ["Jobs", (r) => r.jobs], ["Hours", (r) => hrs(r.hours)], ["Passed", (r) => num(r.good_u, 0)], ["Rejected", (r) => num(r.waste_u, 0)], ["Reject %", (r) => pct(r.reject_pct)]]} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const FIXABLE_REASON = "rejected more than issued";

// AVT inspection rows whose figures cannot be right (rejected more than issued): counted as jobs, left out of
// passed/rejected counts and efficiency, and listed, so one bad row does not swing a whole month's figures.
function Excluded({ ex, isAdmin, onIncluded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const include = (r) => {
    setBusy(r.job_id);
    api.post("/dashboard/production/override", { job_id: r.job_id, kind: "avt" })
      .then(() => { toast.success(`${r.bpr} now counted in the figures`); onIncluded?.(); })
      .catch(() => toast.error("Could not include this record"))
      .finally(() => setBusy(null));
  };
  return (
    <div className="border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900" data-testid="avt-excluded">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          <b>{ex.count.toLocaleString("en-IN")}</b> AVT record{ex.count === 1 ? "" : "s"} left out of the figures: either rejected is more than
          issued (a register row typed into the wrong columns; these still count as jobs) or the record has no date.
        </span>
        <button type="button" onClick={() => setOpen(!open)} className="underline" data-testid="avt-excluded-toggle">{open ? "Hide" : "Show which"}</button>
      </div>
      {open && (
        <div className="mt-2 max-h-56 overflow-auto bg-white border border-amber-200" data-testid="avt-excluded-list">
          <table className="w-full text-[11px]">
            <tbody>
              {ex.rows.map((r) => (
                <tr key={`${r.job_id}-${r.date}`} className="border-t border-amber-100">
                  <td className="px-2 py-1 font-mono-num"><Link to={`/orders/${r.job_id}?tab=production_flow&stage=avt`} className="underline">{r.bpr}</Link></td>
                  <td className="px-2 py-1 font-mono-num">{r.date}</td>
                  <td className="px-2 py-1">{r.reason}</td>
                  <td className="px-2 py-1 text-right">
                    {isAdmin && r.reason === FIXABLE_REASON && (
                      <button type="button" onClick={() => include(r)} disabled={busy === r.job_id}
                        className="underline text-emerald-700 disabled:opacity-50" data-testid={`avt-excluded-add-${r.job_id}`}>
                        {busy === r.job_id ? "Adding…" : "Add anyway"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ex.count > ex.rows.length && <div className="px-2 py-1 text-neutral-500">Latest {ex.rows.length} shown of {ex.count.toLocaleString("en-IN")}.</div>}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent, help, testid }) {
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3" data-testid={testid} title={help}>
      <div className="label-caps text-[10px] leading-tight flex items-start gap-1">
        <span>{label}</span>
        {help && <Info size={11} className="shrink-0 text-neutral-400 mt-px" aria-label={help} />}
      </div>
      <div className={`font-mono-num text-lg font-bold mt-1.5 tracking-tight ${accent || "text-neutral-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5 leading-snug">{sub}</div>}
    </div>
  );
}

function Chart({ title, children, testid, key2 }) {
  return (
    <div className="border border-neutral-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps text-[10px]">{title}</div>
        {key2}
      </div>
      <ResponsiveContainer width="100%" height={280}>{children}</ResponsiveContainer>
    </div>
  );
}

function Table({ title, rows, cols, empty, testid, maxBodyHeight }) {
  return (
    <div className="border border-neutral-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-neutral-200 label-caps">{title}</div>
      <div className={`overflow-x-auto ${maxBodyHeight ? "overflow-y-auto" : ""}`} style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}>
        <table className="w-full text-sm">
          <thead className={`bg-neutral-50 label-caps text-[10px] ${maxBodyHeight ? "sticky top-0 z-10" : ""}`}>
            <tr>{cols.map(([h], i) => <th key={h} className={`px-3 py-2 ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-neutral-100">
                {cols.map(([h, f], i) => <td key={h} className={`px-3 py-2 ${i === 0 ? "text-left font-medium" : "text-right font-mono-num text-xs"}`}>{f(r)}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-neutral-400 text-sm">{empty}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
